package or.garden.manager.domain

import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.isSuccess
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import or.garden.manager.domain.GardenHttpClient.GARDEN_CLIENT
import or.garden.manager.domain.model.AiSuggestionsData
import or.garden.manager.domain.model.ApiResponse
import or.garden.manager.domain.model.CreatePlantedCropRequest
import or.garden.manager.domain.model.GroqRequest
import or.garden.manager.domain.model.PlantedCrop
import or.garden.manager.domain.model.PlantingSuggestionsResponse
import or.garden.manager.domain.model.SavePlantedCropsWithSuggestionsRequest

internal class PlantedCropApi {
    suspend fun getPlantedCrops(bedId: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get("${GardenHttpClient.baseUrl}/beds/$bedId/planted-crops")
    }

    // Send a list to replace existing planted crops
    suspend fun replacePlantedCrops(
        bedId: Int,
        requests: List<CreatePlantedCropRequest>,
        aiSuggestions: AiSuggestionsData? = null
    ): Result<HttpResponse> = runCatching {
        val requestBody = SavePlantedCropsWithSuggestionsRequest(
            plantedCrops = requests,
            aiSuggestions = aiSuggestions
        )
        return@runCatching GARDEN_CLIENT.post("${GardenHttpClient.baseUrl}/beds/$bedId/planted-crops") {
            setBody(requestBody)
        }
    }

    suspend fun getGroqSuggestions(request: GroqRequest): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.post("${GardenHttpClient.baseUrl}/ai") {
            setBody(request)
        }
    }
}

object PlantedCropRepository {
    fun getPlantedCrops(bedId: Int): Flow<ResultState<ApiResponse<PlantingSuggestionsResponse>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = PlantedCropApi().getPlantedCrops(bedId).getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.bodyAsText() ?: "Error"))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to get planted crops. Please try again!"))
        }
    }

    fun replacePlantedCrops(
        bedId: Int,
        requests: List<CreatePlantedCropRequest>,
        aiSuggestions: AiSuggestionsData? = null
    ): Flow<ResultState<ApiResponse<PlantingSuggestionsResponse>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = PlantedCropApi().replacePlantedCrops(bedId, requests, aiSuggestions).getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.bodyAsText() ?: "Error"))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to create planted crops. Please try again!"))
        }
    }

    fun getGroqSuggestions(request: GroqRequest): Flow<ResultState<PlantingSuggestionsResponse>> = flow {
        emit(ResultState.Loading)
        try {
            val response = PlantedCropApi().getGroqSuggestions(request).getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.bodyAsText() ?: "Error"))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to get Groq suggestions. Please try again!"))
        }
    }
}

object PlantedCropService {
    fun getPlantedCrops(bedId: Int, result: (ResultState<ApiResponse<PlantingSuggestionsResponse>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            PlantedCropRepository.getPlantedCrops(bedId).collect { result(it) }
        }
    }

    fun replacePlantedCrops(
        bedId: Int,
        requests: List<CreatePlantedCropRequest>,
        aiSuggestions: AiSuggestionsData? = null,
        result: (ResultState<ApiResponse<PlantingSuggestionsResponse>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            PlantedCropRepository.replacePlantedCrops(bedId, requests, aiSuggestions).collect { result(it) }
        }
    }

    fun getGroqSuggestions(request: GroqRequest, result: (ResultState<PlantingSuggestionsResponse>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            PlantedCropRepository.getGroqSuggestions(request).collect { result(it) }
        }
    }
}
